/**
 * Corrections History Display Component
 * Shows audit trail of field-level corrections for a daily log
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { History } from "lucide-react";

interface Correction {
  id: number;
  fieldName: string;
  originalValue: string | null;
  correctedValue: string;
  correctionReason: string;
  correctedBy: number;
  correctedAt: string;
}

interface CorrectionsHistoryProps {
  corrections: Correction[];
}

export function CorrectionsHistory({ corrections }: CorrectionsHistoryProps) {
  if (!corrections || corrections.length === 0) {
    return null;
  }

  // Map field names to friendly labels
  const fieldLabels: Record<string, string> = {
    meterReadStart: "Meter Read Start",
    meterReadEnd: "Meter Read End",
    gallonsPumped: "Gallons Pumped",
    gpm: "GPM",
    hours: "Hours",
    logTime: "Log Time",
    operatorInitial: "Operator Initial",
    remarks: "Remarks",
  };

  const formatValue = (value: string | null) => {
    if (value === null || value === "") return "(empty)";
    return value;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="w-4 h-4" />
          Corrections History ({corrections.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {corrections.map((correction, index) => (
            <div
              key={correction.id}
              className="border-l-2 border-blue-500 pl-4 py-2 space-y-2"
            >
              <div className="flex items-center gap-2">
                <Badge variant="outline">
                  {fieldLabels[correction.fieldName] || correction.fieldName}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {formatDate(correction.correctedAt)}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Original:</span>
                  <p className="font-mono">{formatValue(correction.originalValue)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Corrected:</span>
                  <p className="font-mono font-semibold">{formatValue(correction.correctedValue)}</p>
                </div>
              </div>

              <div className="text-sm">
                <span className="text-muted-foreground">Reason:</span>
                <p className="mt-1 text-sm">{correction.correctionReason}</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
